<template>
  <!-- Click-away backdrop -->
  <div v-if="open" class="fixed inset-0 z-30" :class="pinned ? 'xl:hidden' : ''" @click="$emit('close')" />

  <aside
    class="fixed right-0 top-16 bottom-0 w-[240px] z-40 bg-gray-900 border-l border-t border-t-gray-700 border-gray-800 flex flex-col select-none"
    :class="open ? '' : 'hidden xl:flex'"
  >
    <!-- ===== Manage Friends view ===== -->
    <template v-if="view === 'friends'">
      <div class="flex items-center gap-2 p-4 border-b border-gray-800">
        <button @click="view = 'main'" class="text-gray-400 hover:text-white transition-colors">
          <ChevronLeftIcon class="w-5 h-5" />
        </button>
        <h3 class="text-text-primary font-semibold text-sm">{{ $t('layout.profile_sidebar.manage_friends') }}</h3>
      </div>

      <div class="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
        <!-- Your friend code -->
        <div>
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">{{ $t('layout.profile_sidebar.your_friend_code') }}</p>
          <button
            @click="copyFriendCode"
            class="w-full flex items-center justify-between rounded-lg bg-gray-900 border border-gray-800 px-3 py-2 hover:border-gray-700 transition-colors"
            :title="$t('layout.profile_sidebar.copy_friend_code')"
          >
            <span class="font-mono text-sm text-text-primary" :class="{ 'blur-sm': user.streamer_mode }">{{ user.friend_code }}</span>
            <CheckIcon v-if="codeCopied" class="w-4 h-4 text-green-400" />
            <DocumentDuplicateIcon v-else class="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <!-- Add friend -->
        <div>
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">{{ $t('layout.profile_sidebar.add_friend') }}</p>
          <form @submit.prevent="addFriend" class="flex gap-2">
            <input
              v-model="friendCodeInput"
              type="text"
              :placeholder="$t('layout.profile_sidebar.friend_code_placeholder')"
              class="flex-1 min-w-0 rounded-lg bg-gray-900 border border-gray-800 px-3 py-2 text-sm text-text-primary placeholder-gray-600 focus:outline-none focus:border-primary-600"
            />
            <button
              type="submit"
              :disabled="!friendCodeInput || addingFriend"
              class="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $t('layout.profile_sidebar.add') }}
            </button>
          </form>
          <p v-if="friendMessage" class="text-xs mt-2" :class="friendMessageIsError ? 'text-red-400' : 'text-green-400'">
            {{ friendMessage }}
          </p>
        </div>

        <!-- Pending requests -->
        <div v-if="friendRequests.length">
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">{{ $t('layout.profile_sidebar.requests_count', { count: friendRequests.length }) }}</p>
          <div v-for="request in friendRequests" :key="request.id" class="flex items-center gap-3 py-2">
            <router-link
              :to="`/user/${request.user.uuid}`"
              @click="$emit('close')"
              class="flex items-center gap-3 flex-1 min-w-0 group/link"
            >
              <UserAvatar :user="request.user" />
              <span class="text-text-primary text-sm truncate group-hover/link:underline">{{ request.user.display_name }}</span>
            </router-link>
            <button
              @click="acceptRequest(request)"
              class="w-7 h-7 rounded-full bg-gray-800 hover:bg-green-700 flex items-center justify-center text-green-400 hover:text-white transition-colors"
              :title="$t('layout.profile_sidebar.accept')"
            >
              <CheckIcon class="w-4 h-4" />
            </button>
            <button
              @click="removeFriendship(request.id)"
              class="w-7 h-7 rounded-full bg-gray-800 hover:bg-red-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              :title="$t('layout.profile_sidebar.decline')"
            >
              <XMarkIcon class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Friends list -->
        <div>
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">{{ $t('layout.profile_sidebar.friends_count', { count: acceptedFriends.length }) }}</p>
          <p v-if="!acceptedFriends.length" class="text-sm text-gray-500">{{ $t('layout.profile_sidebar.no_friends') }}</p>
          <div v-for="friendship in acceptedFriends" :key="friendship.id" class="flex items-center gap-3 py-2 group">
            <router-link
              :to="`/user/${friendship.friend.uuid}`"
              @click="$emit('close')"
              class="flex items-center gap-3 flex-1 min-w-0 group/link"
            >
              <UserAvatar :user="friendship.friend" :online="isOnline(friendship.friend)" />
              <span class="text-text-primary text-sm truncate group-hover/link:underline">{{ friendship.friend.display_name }}</span>
            </router-link>
            <button
              @click="friendPendingRemoval = friendship"
              class="w-7 h-7 rounded-full bg-gray-800 hover:bg-red-700 items-center justify-center text-gray-400 hover:text-white transition-colors hidden group-hover:flex"
              :title="$t('layout.profile_sidebar.remove_friend')"
            >
              <MinusIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- ===== Main view ===== -->
    <template v-else>
      <div class="flex-1 overflow-y-auto space-y-4">
        <!-- Tier / upgrade banner -->
        <div class="p-4 border-b border-gray-800 space-y-2">
          <div class="flex items-center justify-between rounded-lg px-4 py-2.5" :class="tier.banner">
            <span class="text-sm font-medium" :class="tier.text">{{ tier.label }}</span>
            <router-link
              to="/upgrade"
              @click="$emit('close')"
              class="text-sm font-light rounded-md px-2 py-1 -my-1 -mx-2 transition-colors hover:bg-white/10"
              :class="tier.text"
            >
              {{ tier.cta }} →
            </router-link>
          </div>
          <div v-if="user.is_beta_tester" class="flex items-center justify-between rounded-lg px-4 py-2.5 bg-danger-800/40">
            <span class="text-sm font-medium text-danger-400">Beta Tester</span>
            <router-link
              to="/settings#beta-access"
              @click="$emit('close')"
              class="text-sm font-light rounded-md px-2 py-1 -my-1 -mx-2 transition-colors hover:bg-white/10 text-danger-400"
            >
              Settings →
            </router-link>
          </div>
        </div>

        <!-- Menu -->
        <nav class="px-2 pb-4 border-b border-gray-800">
          <router-link :to="`/user/${userLink}`" class="sidebar-item" @click="$emit('close')">
            <UserCircleIcon class="w-5 h-5 text-gray-400" />
            {{ $t('layout.nav.my_profile') }}
          </router-link>
          <router-link v-if="user.access_level > 2" to="/team" class="sidebar-item" @click="$emit('close')">
            <UserGroupIcon class="w-5 h-5 text-gray-400" />
            {{ $t('layout.nav.my_team') }}
          </router-link>
          <router-link to="/settings" class="sidebar-item" @click="$emit('close')">
            <Cog6ToothIcon class="w-5 h-5 text-gray-400" />
            {{ $t('layout.profile_sidebar.account_settings') }}
          </router-link>
          <router-link v-if="canUseV5(user)" to="/v5" class="sidebar-item" @click="$emit('close')">
            <SparklesIcon class="w-5 h-5 text-gray-400" />
            {{ $t('v5.entry.try') }}
          </router-link>
          <router-link to="/friend_codes" class="sidebar-item" @click="$emit('close')">
            <GiftIcon class="w-5 h-5 text-gray-400" />
            {{ $t('layout.profile_sidebar.friend_codes') }}
            <span
              v-if="trialCodeCount > 0"
              class="ml-auto bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
            >{{ trialCodeCount }}</span>
          </router-link>
          <button class="sidebar-item w-full" @click="view = 'friends'">
            <UsersIcon class="w-5 h-5 text-gray-400" />
            {{ $t('layout.profile_sidebar.manage_friends') }}
            <span
              v-if="friendRequests.length > 0"
              class="ml-auto bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
            >{{ friendRequests.length }}</span>
          </button>
        </nav>

        <!-- Party -->
        <div v-if="partyMembers.length" class="p-4 border-b border-gray-800">
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold mb-3">{{ $t('layout.profile_sidebar.party_count', { count: partyMembers.length }) }}</p>
          <div v-for="member in partyMembers" :key="member.uuid" class="flex items-center gap-3 py-2">
            <UserAvatar :user="member" :online="isOnline(member)" />
            <span class="text-text-primary text-sm truncate flex-1">{{ member.username || $t("layout.profile_sidebar.unnamed_player") }}</span>
            <button
              v-if="canRemoveMember(member)"
              @click="removePartyMember(member)"
              class="w-7 h-7 rounded-full bg-gray-800 hover:bg-red-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              :title="member.id === user.id ? $t('layout.profile_sidebar.leave_party') : $t('layout.profile_sidebar.remove_from_party')"
            >
              <MinusCircleIcon class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Online friends -->
        <div class="px-4">
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold mb-3">{{ $t('layout.profile_sidebar.online_count', { count: onlineFriends.length }) }}</p>
          <p v-if="!onlineFriends.length" class="text-sm text-gray-500">{{ $t('layout.profile_sidebar.no_friends_online') }}</p>
          <div v-for="friendship in onlineFriends" :key="friendship.id" class="flex items-center gap-3 py-2">
            <router-link
              :to="`/user/${friendship.friend.uuid}`"
              @click="$emit('close')"
              class="flex items-center gap-3 flex-1 min-w-0 group/link"
            >
              <UserAvatar :user="friendship.friend" :online="true" />
              <span class="text-text-primary text-sm truncate group-hover/link:underline">{{ friendship.friend.display_name }}</span>
            </router-link>
            <button
              v-if="canUseMatchmaking && !isInParty(friendship.friend)"
              @click="inviteToParty(friendship.friend)"
              :disabled="invitedUuids.includes(friendship.friend.uuid)"
              class="w-7 h-7 rounded-full bg-gray-800 hover:bg-primary-600 flex items-center justify-center text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              :title="invitedUuids.includes(friendship.friend.uuid) ? $t('layout.profile_sidebar.invite_sent') : $t('layout.profile_sidebar.invite_to_party')"
            >
              <CheckIcon v-if="invitedUuids.includes(friendship.friend.uuid)" class="w-4 h-4 text-green-400" />
              <PlusCircleIcon v-else class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-2 border-t border-gray-800">
        <button v-if="canImpersonate" @click="$emit('impersonate')" class="sidebar-item w-full text-warning-500">
          <EyeIcon class="w-5 h-5" />
          {{ $t('layout.nav.impersonate') }}
        </button>
        <button v-if="impersonating" @click="$emit('stopImpersonating')" class="sidebar-item w-full text-warning-500">
          <EyeSlashIcon class="w-5 h-5" />
          {{ $t('layout.nav.stop_impersonating') }}
        </button>
        <button @click="$emit('logout')" class="sidebar-item w-full !text-danger-500">
          <ArrowRightStartOnRectangleIcon class="w-5 h-5" />
          {{ $t('layout.nav.sign_out') }}
        </button>
      </div>
    </template>
  </aside>

  <ModalWindow :open="!!friendPendingRemoval" @toggle-modal="friendPendingRemoval = null">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <h1
          class="text-text-primary text-[24px] font-semibold flex gap-3 items-center flex-1"
        >
          <img src="../../assets/icons/error.svg" alt="" class="w-8 h-8" />
          {{ $t('layout.profile_sidebar.remove_friend') }}
        </h1>
        <button
          @click="friendPendingRemoval = null"
          class="text-text-primary text-[24px] font-semibold cursor-pointer"
        >
          <XMarkIcon class="w-6 h-6 text-text-secondary" />
        </button>
      </div>

      <p class="text-text-secondary text-sm">
        {{ $t('layout.profile_sidebar.confirm_remove_friend', { name: friendPendingRemoval?.friend?.display_name }) }}
      </p>
      <div class="flex gap-4">
        <button
          @click="confirmRemoveFriend"
          :disabled="removingFriend"
          class="bg-danger-400 text-danger-950 px-4 py-2 rounded-md font-semibold hover:bg-danger-300 w-full duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ $t('layout.profile_sidebar.confirm_remove') }}
        </button>
        <button
          @click="friendPendingRemoval = null"
          class="bg-gray-800 text-text-primary px-4 py-2 rounded-md font-medium hover:bg-gray-700 w-full duration-300"
        >
          {{ $t('layout.profile_sidebar.keep_friend') }}
        </button>
      </div>
    </div>
  </ModalWindow>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  GiftIcon,
  UsersIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  MinusIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  ChevronLeftIcon,
  DocumentDuplicateIcon,
  ArrowRightStartOnRectangleIcon,
  EyeIcon,
  EyeSlashIcon,
  SparklesIcon,
} from '@heroicons/vue/24/solid';
import UserAvatar from './UserAvatar.vue';
import { canUseV5 } from '../../v5/access';
import ModalWindow from '../ModalWindow.vue';
import useProfileSidebar from '../../composables/useProfileSidebar';

const props = defineProps({
  user: { type: Object, required: true },
  canImpersonate: { type: Boolean, default: false },
  impersonating: { type: Boolean, default: false },
  open: { type: Boolean, default: true },
  pinned: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'openUpgradeModal', 'logout', 'impersonate', 'stopImpersonating']);

const { t } = useI18n();

const {
  view, friendRequests, trialCodeCount, friendCodeInput, addingFriend, friendMessage, friendMessageIsError,
  codeCopied, invitedUuids, friendPendingRemoval, removingFriend, canUseMatchmaking, partyMembers,
  acceptedFriends, isOnline, onlineFriends, userLink, isInParty, canRemoveMember, addFriend, acceptRequest,
  removeFriendship, confirmRemoveFriend, inviteToParty, removePartyMember, copyFriendCode,
} = useProfileSidebar(props);

// Labels/colors match getRole()/getRoleColor() on the profile page.
// Static class strings so Tailwind's JIT keeps them.
const TIERS = {
  5: {
    label: 'layout.tier.admin', text: 'text-red-400', banner: 'bg-red-900/40 border-red-800/60', cta: 'layout.nav.manage',
  },
  4: {
    label: 'layout.tier.pro_tier', text: 'text-yellow-400', banner: 'bg-yellow-900/40 border-yellow-800/60', cta: 'common.upgrade',
  },
  3: {
    label: 'layout.tier.team_tier', text: 'text-green-400', banner: 'bg-green-900/40 border-green-800/60', cta: 'layout.nav.manage',
  },
  2: {
    label: 'layout.tier.competitor_tier', text: 'text-blue-400', banner: 'bg-blue-900/40 border-blue-800/60', cta: 'common.upgrade',
  },
  1: {
    label: 'layout.tier.player_tier', text: 'text-indigo-400', banner: 'bg-indigo-900/40 border-indigo-800/60', cta: 'common.upgrade',
  },
  0: {
    label: 'layout.tier.free_user', text: 'text-gray-400', banner: 'bg-gray-800/40 border-gray-700/60', cta: 'common.upgrade',
  },
};
const tier = computed(() => {
  const found = TIERS[props.user.access_level] ?? TIERS[0];
  return { ...found, label: t(found.label), cta: t(found.cta) };
});
</script>

<style scoped>
.sidebar-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #9cadc4;
  transition: background-color 0.15s, color 0.15s;
  text-align: left;
}
.sidebar-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #eff2f5;
}
</style>
